/**
 * API client para el módulo de Merchants del Backoffice Operativo.
 * Consume endpoints /api/v1/bo/merchants del backend.
 *
 * @taskId T468
 * @see functional/Specs/specs_cycle_4.md HU-26-FE, HU-71-BE, HU-72-BE
 */

import { authFetch } from '@flexibilitypay/shared';

import type {
  Merchant,
  Merchant360,
  MerchantAuditEntry,
  MerchantEditPayload,
  MerchantErrorCode,
  MerchantListParams,
  MerchantListResponse,
} from '../features/merchants/types/merchant.types';

// ─── Error handling (RULE-11) ───────────────────────────────────────────────

/**
 * Error tipificado de la API de Merchants.
 * No expone detalles internos al UI (RULE-25).
 */
export class MerchantApiError extends Error {
  constructor(
    public readonly code: MerchantErrorCode,
    public readonly httpStatus: number,
  ) {
    super(code);
    this.name = 'MerchantApiError';
  }
}

interface ErrorBodyShape {
  code?: string;
  message?: string;
  error?: { code?: string; message?: string };
}

function extractErrorCode(body: ErrorBodyShape | null): string | undefined {
  if (!body) return undefined;
  if (typeof body.code === 'string') return body.code;
  if (body.error && typeof body.error.code === 'string') return body.error.code;
  return undefined;
}

async function safeJson(response: Response): Promise<ErrorBodyShape | null> {
  try {
    return (await response.json()) as ErrorBodyShape;
  } catch {
    return null;
  }
}

const KNOWN_ERROR_CODES: MerchantErrorCode[] = [
  'MERCHANT_NOT_FOUND',
  'CAMPO_NO_EDITABLE',
  'FORBIDDEN',
  'VALIDATION_ERROR',
  'TENANT_MISMATCH',
  'MOTIVO_REQUERIDO',
  'AUTH_INVALID_TOKEN',
];

function isMerchantErrorCode(code: string | undefined): code is MerchantErrorCode {
  return KNOWN_ERROR_CODES.includes(code as MerchantErrorCode);
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (response.ok) {
    return response.json() as Promise<T>;
  }
  const body = await safeJson(response);
  const code = extractErrorCode(body);
  throw new MerchantApiError(
    isMerchantErrorCode(code) ? code : 'UNKNOWN',
    response.status,
  );
}

// ─── API endpoints ──────────────────────────────────────────────────────────

const BASE_PATH = '/bo/merchants';

/**
 * Obtiene listado paginado de merchants con filtros opcionales.
 * @endpoint GET /api/v1/bo/merchants
 * @rule P-14/P-15 — Aislamiento por tenant (X-Tenant-Id en authFetch)
 */
export async function getMerchants(
  params: MerchantListParams,
): Promise<MerchantListResponse> {
  const qs = new URLSearchParams({
    page: String(params.page),
    size: String(params.size),
  });

  if (params.estado) {
    qs.set('estado', params.estado);
  }
  if (params.search) {
    qs.set('search', params.search);
  }

  const response = await authFetch(`${BASE_PATH}?${qs.toString()}`);
  return handleResponse<MerchantListResponse>(response);
}

/**
 * Obtiene detalle básico de un merchant.
 * @endpoint GET /api/v1/bo/merchants/:id
 */
export async function getMerchant(id: string): Promise<Merchant> {
  const response = await authFetch(`${BASE_PATH}/${encodeURIComponent(id)}`);
  return handleResponse<Merchant>(response);
}

/**
 * Obtiene vista 360 consolidada de un merchant.
 * Incluye cuentas, clientes asociados y actividad reciente.
 * @endpoint GET /api/v1/bo/merchants/:id/360
 */
export async function getMerchant360(id: string): Promise<Merchant360> {
  const response = await authFetch(`${BASE_PATH}/${encodeURIComponent(id)}/360`);
  return handleResponse<Merchant360>(response);
}

/**
 * Obtiene historial de auditoría de cambios del merchant.
 * @endpoint GET /api/v1/bo/merchants/:id/audit
 * @rule P-03 — Trazabilidad total
 */
export async function getMerchantAudit(id: string): Promise<MerchantAuditEntry[]> {
  const response = await authFetch(`${BASE_PATH}/${encodeURIComponent(id)}/audit`);
  return handleResponse<MerchantAuditEntry[]>(response);
}

/**
 * Actualiza datos editables de un merchant.
 * @endpoint PATCH /api/v1/bo/merchants/:id
 * @rule RULE-29 — Solo PSP_ADMIN puede cambiar estado
 * @rule RULE-16 — Motivo obligatorio en cambio de estado
 * @rule P-03 — Cambio queda trazado con actor y timestamp
 */
export async function updateMerchant(
  id: string,
  payload: MerchantEditPayload,
): Promise<Merchant> {
  const response = await authFetch(`${BASE_PATH}/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return handleResponse<Merchant>(response);
}

/**
 * Exporta listado de merchants a CSV.
 * @endpoint GET /api/v1/bo/merchants/export
 * @returns Blob con contenido CSV
 */
export async function exportMerchantsCsv(
  params: Omit<MerchantListParams, 'page' | 'size'>,
): Promise<Blob> {
  const qs = new URLSearchParams();
  if (params.estado) {
    qs.set('estado', params.estado);
  }
  if (params.search) {
    qs.set('search', params.search);
  }

  const response = await authFetch(`${BASE_PATH}/export?${qs.toString()}`);
  if (!response.ok) {
    const body = await safeJson(response);
    const code = extractErrorCode(body);
    throw new MerchantApiError(
      isMerchantErrorCode(code) ? code : 'UNKNOWN',
      response.status,
    );
  }
  return response.blob();
}
